/**
 * Schemes that must never load as a page and must never leave the app as an
 * "external protocol". File-identity schemes are here because they address
 * on-disk assets and are not registered with the OS (见 20 / 22).
 */
const BLOCKED_BROWSER_PROTOCOLS = new Set([
  'javascript:',
  'data:',
  'file:',
  'blob:',
  'about:',
  'chrome:',
  'chrome-extension:',
  'devtools:',
  'view-source:',
  'ws:',
  'wss:',
  'hibro-assets:',
  'hibro-app:',
]);

export type BrowserNavigationClass =
  | { readonly kind: 'page'; readonly url: string }
  | {
      readonly kind: 'external-protocol';
      readonly url: string;
      readonly scheme: string;
    }
  | { readonly kind: 'blocked'; readonly reason: string };

function parseBrowserCandidate(input: string): URL {
  const trimmed = input.trim();
  if (!trimmed) {
    throw new Error('Browser URL is empty');
  }
  const explicitScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed);
  const hostWithPort = /^(?:[^:/?#]+|\[[^\]]+\]):\d+(?:[/?#]|$)/.test(trimmed);
  const localHost =
    /^(?:localhost|127(?:\.\d+){3}|\[::1\])(?::\d+)?(?:[/?#]|$)/i.test(trimmed);
  const candidate =
    explicitScheme && !hostWithPort
      ? trimmed
      : `${localHost ? 'http' : 'https'}://${trimmed}`;
  return new URL(candidate);
}

/** Normalize and validate a URL before it reaches untrusted browser content. */
export function normalizeBrowserUrl(input: string): string {
  const parsed = parseBrowserCandidate(input);
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`Browser URL scheme is not allowed: ${parsed.protocol}`);
  }
  return parsed.toString();
}

/**
 * Fork a chrome or page navigation: load as a page, hand to the OS, or reject.
 * `normalizeBrowserUrl` remains the "may this become page content?" check.
 */
export function classifyBrowserNavigation(
  input: string,
): BrowserNavigationClass {
  let parsed: URL;
  try {
    parsed = parseBrowserCandidate(input);
  } catch (error) {
    return {
      kind: 'blocked',
      reason: error instanceof Error ? error.message : String(error),
    };
  }
  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    return { kind: 'page', url: parsed.toString() };
  }
  if (BLOCKED_BROWSER_PROTOCOLS.has(parsed.protocol.toLowerCase())) {
    return {
      kind: 'blocked',
      reason: `Browser URL scheme is not allowed: ${parsed.protocol}`,
    };
  }
  return {
    kind: 'external-protocol',
    url: parsed.toString(),
    scheme: parsed.protocol.slice(0, -1).toLowerCase(),
  };
}

export type BrowserNavigationSource = 'chrome' | 'page' | 'redirect';

export type BrowserNavigationDecision =
  | { readonly action: 'load'; readonly url: string }
  | {
      readonly action: 'open-external';
      readonly url: string;
      readonly scheme: string;
    }
  | {
      readonly action: 'prompt-protocol';
      readonly url: string;
      readonly scheme: string;
    }
  | { readonly action: 'reject'; readonly reason: string };

/**
 * Apply the built-in browser's navigation policy (见 22): chrome address-bar
 * input may leave the app; a page click must confirm; a redirect never does.
 */
export function decideBrowserNavigation(
  input: string,
  source: BrowserNavigationSource,
): BrowserNavigationDecision {
  const classified = classifyBrowserNavigation(input);
  if (classified.kind === 'page') {
    return { action: 'load', url: classified.url };
  }
  if (classified.kind === 'blocked') {
    return { action: 'reject', reason: classified.reason };
  }
  if (source === 'redirect') {
    return {
      action: 'reject',
      reason: `External protocol on redirect is not allowed: ${classified.scheme}`,
    };
  }
  if (source === 'chrome') {
    return {
      action: 'open-external',
      url: classified.url,
      scheme: classified.scheme,
    };
  }
  return {
    action: 'prompt-protocol',
    url: classified.url,
    scheme: classified.scheme,
  };
}

/** Short tab label from a browser URL — hostname, else a generic fallback. */
export function browserEditorTabTitle(url: string): string {
  try {
    return new URL(url).hostname || 'Browser';
  } catch {
    return 'Browser';
  }
}

/** Produce a log-safe target that never includes path, query, or credentials. */
export function describeBrowserTarget(input: string): string {
  try {
    const parsed = new URL(input);
    return parsed.origin === 'null' ? parsed.protocol : parsed.origin;
  } catch {
    return '<invalid-url>';
  }
}

/**
 * A link reduced to what may be written to the log: host and path, never the
 * query (见 15 「绝不记录凭证」 / 20 「日志只记 host + path」).
 *
 * The same rule the extension host's own link logging follows, for the same
 * reason — an app link can carry a one-time credential, and the log pipeline
 * persists to disk. Unparseable input yields nothing rather than echoing the raw
 * string back.
 *
 * Keeps the path, unlike {@link describeBrowserTarget}: the callers that log
 * links are answering "which address was routed where", and on the desktop the
 * workbench's own custom scheme has no tuple origin to report at all.
 */
export function describeLinkForLog(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return '<unparseable link>';
  }
}
